import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../components/modal-escape-guard/modal-escape-guard.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  AGENT_TOKEN_MAX_DAYS,
  CreatePatRequest,
  EXTRA_ACTION_DEPENDENCIES,
  PatAccessLevel,
  PatScopeGroup,
  groupSelectablePatScopes,
  higherLevel,
  isLevelAtLeast,
  scopesForLevel,
  selectableAgentPatScopes,
  selectablePatScopes,
} from '../../models/pat.model';
import { environment } from '../../../environments/environment';
import { AccountService } from '../account.service';

/** Loads the scope catalogue the dialog offers; lets callers such as the agent token page use their own list. */
export type PatScopesSource = () => Observable<string[]>;

@Component({
  selector: 'app-pat-create-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Modal,
    ModalEscapeGuardDirective,
    TranslatePipe,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatRadioModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './pat-create-dialog.component.html',
  styleUrl: './pat-create-dialog.component.scss',
})
export class PatCreateDialogComponent implements OnInit, AfterViewInit {
  readonly visible = input.required<boolean>();
  readonly isBusy = input<boolean>(false);
  readonly createdToken = input<string | null>(null);
  /** Without a source the dialog offers the personal catalogue of the signed-in user. */
  readonly scopesSource = input<PatScopesSource | null>(null);
  /** Agent tokens need an expiry of at most {@link AGENT_TOKEN_MAX_DAYS} days. */
  readonly expiryRequired = input<boolean>(false);
  /** Hides the name field and sends this name, e.g. `ki-team` for the bulk creation of agent tokens. */
  readonly fixedName = input<string | null>(null);
  readonly titleKey = input<string>('pat.dialog.title');

  readonly create = output<CreatePatRequest>();
  readonly visibleChange = output<boolean>();
  readonly closed = output<void>();

  @ViewChild('content') private contentTemplate!: TemplateRef<unknown>;

  private readonly fb = inject(NonNullableFormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly accountService = inject(AccountService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly scopeGroups = signal<PatScopeGroup[]>([]);
  readonly catalogState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  private readonly levels = signal<Record<string, PatAccessLevel>>({});
  private readonly extraScopes = signal<Set<string>>(new Set());

  readonly selectedScopes = computed<string[]>(() => {
    const levels = this.levels();
    const extras = this.extraScopes();
    const scopes: string[] = [];
    for (const group of this.scopeGroups()) {
      scopes.push(...scopesForLevel(group.resource, levels[group.resource] ?? 'none'));
      for (const action of group.extraActions) {
        const scope = `${group.resource}:${action}`;
        if (extras.has(scope)) scopes.push(scope);
      }
    }
    return scopes;
  });

  readonly copied = signal(false);
  readonly curlCopied = signal(false);

  readonly quickstartUrl = environment.patQuickstartUrl;
  readonly swaggerUrl = environment.swaggerUrl;

  /** Copy-paste-ready curl call that authenticates the freshly created token against a read endpoint. */
  readonly curlExample = computed<string>(() => {
    const token = this.createdToken();
    if (!token) return '';
    return `curl -H "Authorization: Bearer ${token}" ${environment.apiBaseUrl}/api/v1/projects`;
  });

  readonly form = this.fb.group({
    name: this.fb.control('', Validators.required),
    expiresAt: this.fb.control<Date | null>(null),
  });

  readonly minExpiryDate = (() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date;
  })();

  readonly maxExpiryDate = (() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + AGENT_TOKEN_MAX_DAYS);
    return date;
  })();

  resolvedTemplate: TemplateRef<unknown> | null = null;

  constructor() {
    effect(() => {
      if (!this.visible()) return;
      untracked(() => {
        const state = this.catalogState();
        if (state === 'idle' || state === 'error') this.loadCatalog();
      });
    });
  }

  ngOnInit(): void {
    this.form.controls.name.setValue(this.fixedName() ?? '');
    if (this.expiryRequired()) {
      this.form.controls.expiresAt.addValidators(Validators.required);
      this.form.controls.expiresAt.updateValueAndValidity();
    }
  }

  ngAfterViewInit(): void {
    this.resolvedTemplate = this.contentTemplate;
    this.cdr.detectChanges();
  }

  get createDisabled(): boolean {
    return this.catalogState() !== 'loaded' || this.form.invalid || this.selectedScopes().length === 0 || this.isBusy();
  }

  resourceLabel(resource: string): string {
    return this.labelOr(`pat.scope.resource.${resource}`, resource);
  }

  extraLabel(resource: string, action: string): string {
    const scope = `${resource}:${action}`;
    return this.labelOr(`pat.scope.${scope}`, scope);
  }

  levelOf(resource: string): PatAccessLevel {
    return this.levels()[resource] ?? 'none';
  }

  /** Minimum level `resource` must keep while a checked extra action depends on it. */
  minLevelFor(resource: string): PatAccessLevel {
    let min: PatAccessLevel = 'none';
    for (const scope of this.extraScopes()) {
      const dependency = EXTRA_ACTION_DEPENDENCIES[scope];
      if (dependency?.resource === resource) min = higherLevel(min, dependency.level);
    }
    return min;
  }

  levelOptionDisabled(resource: string, level: PatAccessLevel): boolean {
    return !isLevelAtLeast(level, this.minLevelFor(resource));
  }

  setLevel(resource: string, level: PatAccessLevel): void {
    const min = this.minLevelFor(resource);
    const applied = isLevelAtLeast(level, min) ? level : min;
    this.levels.update((current) => ({ ...current, [resource]: applied }));
  }

  isExtraSelected(scope: string): boolean {
    return this.extraScopes().has(scope);
  }

  toggleExtra(scope: string, checked: boolean): void {
    this.extraScopes.update((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(scope);
      } else {
        next.delete(scope);
      }
      return next;
    });

    const dependency = EXTRA_ACTION_DEPENDENCIES[scope];
    if (checked && dependency) {
      this.levels.update((current) => ({
        ...current,
        [dependency.resource]: higherLevel(current[dependency.resource] ?? 'none', dependency.level),
      }));
    }
  }

  onCreate(): void {
    if (this.createDisabled) return;
    const expiresAt = this.form.controls.expiresAt.value;
    this.create.emit({
      name: this.form.controls.name.value.trim(),
      scopes: this.selectedScopes(),
      expiresAt: expiresAt ? expiresAt.toISOString() : null,
    });
  }

  copyToken(): void {
    this.copyToClipboard(this.createdToken(), this.copied);
  }

  copyCurl(): void {
    this.copyToClipboard(this.curlExample(), this.curlCopied);
  }

  private copyToClipboard(text: string | null, copiedFlag: WritableSignal<boolean>): void {
    if (!text || !navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(
      () => copiedFlag.set(true),
      () => {},
    );
  }

  onClose(): void {
    this.resetForm();
    this.visibleChange.emit(false);
    this.closed.emit();
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.resetForm();
      this.closed.emit();
    }
    this.visibleChange.emit(visible);
  }

  retryCatalog(): void {
    this.loadCatalog();
  }

  private resetForm(): void {
    this.form.reset({ name: this.fixedName() ?? '', expiresAt: null });
    this.levels.set({});
    this.extraScopes.set(new Set());
    this.copied.set(false);
    this.curlCopied.set(false);
  }

  private loadCatalog(): void {
    this.catalogState.set('loading');
    const agentSource = this.scopesSource();
    const source = agentSource ?? (() => this.accountService.getTokenScopes());
    const selectable = agentSource ? selectableAgentPatScopes : selectablePatScopes;
    source()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (catalog) => {
          const groups = groupSelectablePatScopes(selectable(catalog));
          this.scopeGroups.set(groups);
          this.catalogState.set(groups.length > 0 ? 'loaded' : 'error');
        },
        error: () => this.catalogState.set('error'),
      });
  }

  private labelOr(key: string, fallback: string): string {
    const label = this.translate.instant(key);
    return label === key ? fallback : label;
  }
}
